import Seo from "@/components/feature/Seo";
import { useMemo, useState } from "react";
import Container from "@/components/base/Container";
import PageHero from "@/components/base/PageHero";
import SectionHeading from "@/components/base/SectionHeading";
import Button from "@/components/base/Button";
import GalleryGrid from "./components/GalleryGrid";
import Lightbox from "./components/Lightbox";
import { useGaleria } from "@/hooks/usePublicContent";
import { galleryCategoryOptions } from "@/lib/galleryMeta";
import type { GalleryCategory } from "@/types";

export default function Trabajos() {
  const gallery = useGaleria();
  const items = useMemo(
    () => gallery.filter((item) => item.activo).sort((a, b) => a.orden - b.orden),
    [gallery],
  );
  const [categoria, setCategoria] = useState<GalleryCategory | "todas">("todas");
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const filtered = useMemo(
    () => (categoria === "todas" ? items : items.filter((item) => item.categoria === categoria)),
    [categoria, items],
  );

  const countByCategory = useMemo(() => {
    return items.reduce<Record<string, number>>((acc, item) => {
      acc[item.categoria] = (acc[item.categoria] ?? 0) + 1;
      return acc;
    }, {});
  }, [items]);

  return (
    <>
      <Seo
        title="Trabajos realizados"
        description="Explora mudanzas, acarreos, eventos, distribución y transporte ejecutados por AXONIA GROUP. Galería de operaciones realizadas con nuestra flota."
        path="/trabajos"
      />
      <PageHero
        title="Nuestros trabajos"
        subtitle="Mudanzas, acarreos, eventos, distribución y transporte ejecutados por nuestro equipo. Explora cada operación y ábrela en el visor ampliado."
        crumbs={[{ label: "Inicio", to: "/" }, { label: "Trabajos realizados" }]}
        image="https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20a%20white%20logistics%20truck%20being%20loaded%20with%20wrapped%20furniture%20and%20boxes%20at%20a%20clean%20bright%20industrial%20terminal%2C%20professional%20corporate%20transport%20photography%2C%20deep%20blue%20tones%2C%20high%20detail&width=1920&height=700&seq=axonia-hero-trabajos&orientation=landscape"
      />

      <section className="bg-background-50 py-12 md:py-16">
        <Container>
          <SectionHeading
            eyebrow="Galería"
            title="Operaciones realizadas"
            description="Filtra por tipo de trabajo y abre cualquier imagen para verla en detalle."
          />

          <div className="mt-8 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCategoria("todas")}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-label font-medium transition-colors duration-200 cursor-pointer ${
                categoria === "todas"
                  ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                  : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
              }`}
            >
              Todos los trabajos
              <span className="rounded-full bg-background-50/25 px-1.5 text-xs font-semibold">
                {items.length}
              </span>
            </button>
            {galleryCategoryOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setCategoria(option.value)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-label font-medium transition-colors duration-200 cursor-pointer ${
                  categoria === option.value
                    ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                    : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                }`}
              >
                {option.label}
                <span className="rounded-full bg-background-50/25 px-1.5 text-xs font-semibold">
                  {countByCategory[option.value] ?? 0}
                </span>
              </button>
            ))}
          </div>

          {filtered.length > 0 ? (
            <div className="mt-8">
              <GalleryGrid items={filtered} onOpen={setActiveIndex} />
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-image-line text-2xl leading-none" aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 text-base font-heading font-semibold text-foreground-950">
                Aún no hay trabajos en esta categoría
              </h3>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                Prueba con otra categoría o revisa todos los trabajos realizados.
              </p>
            </div>
          )}
        </Container>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <Container>
          <div className="flex flex-col items-center gap-6 rounded-lg border border-background-200 bg-background-50 px-6 py-10 text-center md:px-12">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-roadster-line text-2xl leading-none" aria-hidden="true"></i>
            </span>
            <SectionHeading
              align="center"
              title="¿Quieres que tu próxima operación figure aquí?"
              description="Cuéntanos tu necesidad y coordinamos la unidad ideal para tu mudanza, acarreo, evento o distribución."
            />
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button to="/solicitar-servicio" variant="primary" size="lg" icon="ri-send-plane-line">
                Solicitar servicio
              </Button>
              <Button to="/flota" variant="outline" size="lg" icon="ri-truck-line">
                Ver nuestra flota
              </Button>
            </div>
          </div>
        </Container>
      </section>

      {activeIndex !== null && (
        <Lightbox
          items={filtered}
          activeIndex={activeIndex}
          onClose={() => setActiveIndex(null)}
          onNavigate={setActiveIndex}
        />
      )}
    </>
  );
}